import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { useRef } from 'react';
import { Button, TextField } from '@teachstonellc/component-library';
import { useLoginManager } from 'components/LoginManager';
import { Formik } from 'components/EnhancedForm';
import FocusFirstInvalidField from 'components/FocusFirstInvalidField';
import useFocusOnStepChange from '../useFocusOnStepChange';
import { ButtonWrapper, SignOutIFrame } from './Styled';

import {
  isPhoneLike,
  normalizeLoginInput,
  toInternationalPhone
} from '../loginInput';

const validationSchema = yup.object({
  search_text: yup
    .string()
    .transform(normalizeLoginInput)
    .required(() => i18n.ft(messages.errors.email.required))
    .test(
      'email-or-phone',
      () => i18n.ft(messages.errors.email.required),
      value =>
        !value || isPhoneLike(value) || yup.string().email().isValidSync(value)
    )
});

function LoginLookup() {
  const { setStep, setEmailOrPhone, emailOrPhone } = useLoginManager();
  const searchTextInputRef = useRef<HTMLInputElement>(null);

  useFocusOnStepChange(searchTextInputRef, { select: true });

  function handleSubmit(values: { search_text: string }) {
    const searchText = normalizeLoginInput(values.search_text);

    if (isPhoneLike(searchText)) {
      setEmailOrPhone(toInternationalPhone(searchText));
      setStep('loginWithCode');
    } else {
      setEmailOrPhone(searchText);
      setStep('loginWithPassword');
    }
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={{ search_text: emailOrPhone }}
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {formik => {
        const isError = Boolean(
          formik.touched.search_text && formik.errors.search_text
        );

        return (
          <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <FocusFirstInvalidField
              submitCount={formik.submitCount}
              fields={[{ isInvalid: isError, inputRef: searchTextInputRef }]}
            />

            <TextField
              {...formik.getFieldProps('search_text')}
              ref={searchTextInputRef}
              label={i18n.ft(messages.email)}
              placeholder={i18n.ft(messages.emailPlaceholder)}
              autoComplete="username"
              aria-invalid={isError}
              error={isError ? formik.errors.search_text : undefined}
            />

            <input
              name="password"
              tabIndex={-1}
              type="password"
              autoComplete="current-password"
              style={{ opacity: 0, height: 0, position: 'absolute' }}
            />

            <ButtonWrapper>
              <Button fullWidth variant="primary" shape="rounded" type="submit">
                {i18n.ft(messages.continue)}
              </Button>
            </ButtonWrapper>

            <SignOutIFrame
              title="single logout iframe"
              src={RouteHelpers.getPath('single-logout')}
            />
          </form>
        );
      }}
    </Formik>
  );
}

export default LoginLookup;
